/**
 * The league store the single-file build runs on.
 *
 * Every screen is fully interactive against it — declare a slate, record a trade, run the draft
 * — and it is also the fallback if Firebase is unreachable on draft night, the one evening of
 * the year that cannot afford a blank page.
 *
 * It now KEEPS what you type, which it did not used to.
 *
 * The original reasoning against localStorage was sound as far as it went: this build runs inside
 * sandboxes where browser storage throws, and a store that half-works is worse than one that
 * plainly resets. What was wrong was the conclusion. Somebody working through sixty team names
 * from memory and losing the lot to a refresh is a far worse outcome than a storage backend that
 * is unavailable in some contexts — so the store now persists where it can, and where it cannot it
 * SAYS SO, in the app, next to a button that writes the same data to a file instead.
 *
 * Two rules make that honest rather than half-working:
 *
 *   Availability is probed, not assumed. A real write to a real key at construction, caught. The
 *   answer is exposed as `persistence` so the UI can tell the truth about it.
 *
 *   The bundled snapshots are never persisted. The NFL and college pools are megabytes and ship
 *   with the build; storing a copy would blow the quota to save something already on disk. Only
 *   what a person typed is kept.
 */

import type { PlayerBank } from '../domain/playerBank.js';
import type { LeagueRoles } from './types.js';
import type { ProspectBank } from '../domain/prospectBank.js';
import type { Override } from '../domain/overrides.js';
import type {
  AdminNote, Avatar, DraftPick, DraftPreference, DraftState, FaabEntry, FarmNomination,
  FarmTargetList,
  PickList, GmId,
  InterpretationDoc,
  AuditEntry,
  Docket, KeeperDeclaration, LeagueManager, LeagueSettings, LeagueStore, LiveTradeDoc,
  MockKeeperSet, RuleProposal,
  StoreCollection, TeamPairing, Trade,
} from './types.js';
import { ALL_COLLECTIONS } from './types.js';
import { SEED_SEASON, SEED_SETTINGS_2026 } from './seed.js';
import type { ProxyBallot } from '../domain/proxy.js';

type Listener = (what: StoreCollection) => void;

/** Everything a person has typed, in one serialisable object. */
export interface Snapshot {
  version: 1;
  league: string;
  savedAt: string;
  settings: LeagueSettings[];
  declarations: KeeperDeclaration[];
  preferences: DraftPreference[];
  trades: Trade[];
  faab: FaabEntry[];
  farmNominations: FarmNomination[];
  draftStates: DraftState[];
  picks: DraftPick[];
  notes: AdminNote[];
  interpretations: InterpretationDoc[];
  overrides: Override[];
  mockKeepers: MockKeeperSet[];
  proposals: RuleProposal[];
  /* Optional, like `trades` below and for the same reason — snapshots predate it. */
  proxies?: ProxyBallot[];
  farmTargets?: FarmTargetList[];
  pickLists?: PickList[];
  /**
   * Optional, unlike its neighbours: snapshots saved before trades had a live stage are still on
   * people's disks and in Dropbox, and a required field would make every one of them unreadable
   * for the sake of a collection that is empty most of the year.
   */
  liveTrades?: LiveTradeDoc[];
  /** Optional for the same reason as `liveTrades` — older snapshots predate the log. */
  audit?: AuditEntry[];
  dockets: Docket[];
  teamPairings: TeamPairing[];
  managers: LeagueManager[];
}

export interface Persistence {
  /** False in a sandbox that blocks storage — the UI must say so rather than pretend. */
  available: boolean;
  /** Why not, in words a person can act on. */
  reason?: string;
  savedAt: string | null;
  /** Roughly how much is being kept, for the "is this getting big?" question. */
  bytes: number;
}

const KEY = 'ffl.league.v1';
const LEAGUE = 'united_vtmd_uva_haters';

export class MemoryStore implements LeagueStore {
  readonly kind = 'memory' as const;

  private declarationsBySeason = new Map<number, Map<GmId, KeeperDeclaration>>();
  private prefsBySeason = new Map<number, Map<GmId, DraftPreference>>();
  private proxiesBySeason = new Map<number, Map<GmId, ProxyBallot>>();
  private farmTargetsBySeason = new Map<number, Map<GmId, FarmTargetList>>();
  private pickListsBySeason = new Map<number, Map<GmId, PickList>>();
  private tradesBySeason = new Map<number, Map<string, Trade>>();
  private faabBySeason = new Map<number, Map<string, FaabEntry>>();
  private farmBySeason = new Map<number, Map<string, FarmNomination>>();
  private draftBySeason = new Map<number, DraftState>();
  private picksBySeason = new Map<number, Map<string, DraftPick>>();
  private notesBySeason = new Map<number, Map<string, AdminNote>>();
  private interpBySeason = new Map<number, Map<string, InterpretationDoc>>();
  private overrideBySeason = new Map<number, Map<string, Override>>();
  /** Keyed by the AUTHOR, not by the team guessed about — one document per manager per season. */
  private mockKeepersBySeason = new Map<number, Map<GmId, MockKeeperSet>>();
  private settingsBySeason = new Map<number, LeagueSettings>();
  private bankBySeason = new Map<number, PlayerBank>();
  private prospectsBySeason = new Map<number, ProspectBank>();
  private proposalsBySeason = new Map<number, Map<string, RuleProposal>>();
  private liveTradesBySeason = new Map<number, Map<string, LiveTradeDoc>>();
  private auditBySeason = new Map<number, Map<string, AuditEntry>>();
  private docketBySeason = new Map<number, Docket>();
  /** Season-spanning, keyed `${season}|${TEAM NAME}`. */
  private pairings = new Map<string, TeamPairing>();
  private roster = new Map<GmId, LeagueManager>();
  /** Manager pictures. Small enough that the whole set lives in memory like everything else. */
  private faces = new Map<GmId, Avatar>();
  private listeners = new Set<Listener>();

  /** What the app can tell the user about whether their work is being kept. */
  readonly persistence: Persistence = { available: false, savedAt: null, bytes: 0 };
  private persist: boolean;
  private writeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor({ persist = true }: { persist?: boolean } = {}) {
    this.persist = persist;
    if (persist) this.openStorage();
    // The announced 2026 calendar, shared with the Firestore store so both come up with the same
    // dates. See src/store/seed.ts for why it is a module rather than a literal here.
    this.settingsBySeason.set(SEED_SEASON, { ...SEED_SETTINGS_2026 });

    // Anything saved earlier wins over the seed above.
    if (persist) this.hydrate();
  }

  /* -- keeping it ------------------------------------------------------------------------- */

  /**
   * Probe rather than feature-detect. `typeof localStorage !== 'undefined'` is true in a sandbox
   * that then throws on write, which is exactly the half-working failure this has to avoid.
   */
  private openStorage() {
    try {
      const probe = `${KEY}.probe`;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      this.persistence.available = true;
    } catch (e) {
      this.persistence.available = false;
      this.persistence.reason = e instanceof Error && /quota/i.test(e.message)
        ? 'This browser has no room left for the app to save into.'
        : 'This browser is blocking storage for the page — often a sandboxed preview, a file:// '
          + 'window, or private browsing.';
      this.persist = false;
    }
  }

  private hydrate() {
    if (!this.persistence.available) return;
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return;
    try {
      const snap = JSON.parse(raw) as Snapshot;
      if (snap.version !== 1) return;
      this.load(snap);
      this.persistence.savedAt = snap.savedAt;
      this.persistence.bytes = raw.length;
    } catch {
      // A corrupt blob is not worth crashing the app over, and not worth silently deleting
      // either — leave it in place so it can be recovered by hand if it ever matters.
      this.persistence.reason = 'What was saved could not be read, so the app started empty.';
    }
  }

  /** Debounced: the draft board writes a pick per click and this must not run on every keystroke. */
  private schedule() {
    if (!this.persist || !this.persistence.available) return;
    if (this.writeTimer) clearTimeout(this.writeTimer);
    this.writeTimer = setTimeout(() => this.flush(), 400);
  }

  /** Write now. Called on a debounce, and directly before the page is hidden. */
  flush(): void {
    if (!this.persist || !this.persistence.available) return;
    const snap = this.snapshot();
    const raw = JSON.stringify(snap);
    try {
      window.localStorage.setItem(KEY, raw);
      this.persistence.savedAt = snap.savedAt;
      this.persistence.bytes = raw.length;
      delete this.persistence.reason;
    } catch {
      this.persistence.available = false;
      this.persistence.reason = 'There was no room to save — download a backup instead.';
    }
  }

  /** Everything typed, ready to be written to a file or to storage. */
  snapshot(): Snapshot {
    const flat = <T>(m: Map<number, Map<string, T>>): T[] =>
      [...m.values()].flatMap((b) => [...b.values()]);
    return {
      version: 1,
      league: LEAGUE,
      savedAt: new Date().toISOString(),
      settings: [...this.settingsBySeason.values()],
      declarations: [...this.declarationsBySeason.values()].flatMap((b) => [...b.values()]),
      preferences: [...this.prefsBySeason.values()].flatMap((b) => [...b.values()]),
      proxies: [...this.proxiesBySeason.values()].flatMap((b) => [...b.values()]),
      farmTargets: [...this.farmTargetsBySeason.values()].flatMap((b) => [...b.values()]),
      pickLists: [...this.pickListsBySeason.values()].flatMap((b) => [...b.values()]),
      trades: flat(this.tradesBySeason),
      faab: flat(this.faabBySeason),
      farmNominations: flat(this.farmBySeason),
      draftStates: [...this.draftBySeason.values()],
      picks: flat(this.picksBySeason),
      notes: flat(this.notesBySeason),
      interpretations: flat(this.interpBySeason),
      overrides: flat(this.overrideBySeason),
      mockKeepers: flat(this.mockKeepersBySeason),
      proposals: flat(this.proposalsBySeason),
      liveTrades: flat(this.liveTradesBySeason),
      audit: flat(this.auditBySeason),
      dockets: [...this.docketBySeason.values()],
      teamPairings: [...this.pairings.values()],
      managers: [...this.roster.values()],
    };
  }

  /**
   * Replace everything with a snapshot. Used on start-up and by Restore.
   *
   * Deliberately a replace rather than a merge: a backup is a picture of a moment, and merging
   * two pictures produces a third that never existed. The UI warns before calling it.
   */
  load(snap: Snapshot): void {
    this.declarationsBySeason.clear();
    this.prefsBySeason.clear();
    this.proxiesBySeason.clear();
    this.farmTargetsBySeason.clear();
    this.pickListsBySeason.clear();
    this.tradesBySeason.clear();
    this.faabBySeason.clear();
    this.farmBySeason.clear();
    this.draftBySeason.clear();
    this.picksBySeason.clear();
    this.notesBySeason.clear();
    this.interpBySeason.clear();
    this.overrideBySeason.clear();
    this.mockKeepersBySeason.clear();
    this.proposalsBySeason.clear();
    this.liveTradesBySeason.clear();
    this.auditBySeason.clear();
    this.docketBySeason.clear();
    this.pairings.clear();
    this.roster.clear();

    /**
     * Saved settings win only if somebody actually EDITED them after the seed was written.
     *
     * This bit us. The store persists everything it holds, including the seeded league calendar,
     * so the first time anybody opened the app their browser wrote the seed to localStorage. When
     * the announced dates then changed in a later build, the stale copy loaded straight over the
     * top of the new one and the home screen kept showing a draft date that no longer existed —
     * on the one screen where being wrong about a date matters most.
     *
     * `updatedAt` decides it. A seed carries the date its numbers were announced; a real edit
     * carries the moment it was made. Newer wins, which gives the right answer both ways round: a
     * commissioner's correction survives a redeploy, and a redeployed correction survives a stale
     * cache. Nothing else in the snapshot has this problem, because nothing else is seeded.
     */
    for (const s of snap.settings ?? []) {
      const seeded = this.settingsBySeason.get(s.season);
      const savedAt = new Date(s.updatedAt).getTime();
      const seededAt = seeded ? new Date(seeded.updatedAt).getTime() : -Infinity;
      if (seeded && (!Number.isFinite(savedAt) || savedAt <= seededAt)) continue;
      this.settingsBySeason.set(s.season, s);
    }
    for (const d of snap.declarations ?? []) this.bucketBy(this.declarationsBySeason, d.season).set(d.gm, d);
    for (const p of snap.preferences ?? []) this.bucketBy(this.prefsBySeason, p.season).set(p.gm, p);
    for (const b of snap.proxies ?? []) this.bucketBy(this.proxiesBySeason, b.season).set(b.gm, b);
    for (const t of snap.farmTargets ?? []) {
      this.bucketBy(this.farmTargetsBySeason, t.season).set(t.gm.toUpperCase() as GmId, t);
    }
    for (const l of snap.pickLists ?? []) {
      this.bucketBy(this.pickListsBySeason, l.season).set(l.gm.toUpperCase() as GmId, l);
    }
    for (const t of snap.trades ?? []) this.bucket(this.tradesBySeason, t.season).set(t.id, t);
    for (const e of snap.faab ?? []) this.bucket(this.faabBySeason, e.season).set(e.id, e);
    for (const n of snap.farmNominations ?? []) {
      this.bucket(this.farmBySeason, n.season).set(`${n.round}.${n.slot}`, n);
    }
    for (const d of snap.draftStates ?? []) this.draftBySeason.set(d.season, d);
    for (const p of snap.picks ?? []) this.bucket(this.picksBySeason, p.season).set(String(p.overall), p);
    for (const n of snap.notes ?? []) this.bucket(this.notesBySeason, n.season).set(n.id, n);
    for (const i of snap.interpretations ?? []) this.bucket(this.interpBySeason, i.season).set(i.id, i);
    for (const o of snap.overrides ?? []) this.bucket(this.overrideBySeason, o.season).set(o.id, o);
    for (const m of snap.mockKeepers ?? []) this.bucketBy(this.mockKeepersBySeason, m.season).set(m.gm, m);
    for (const p of snap.proposals ?? []) this.bucket(this.proposalsBySeason, p.season).set(p.id, p);
    for (const t of snap.liveTrades ?? []) this.bucket(this.liveTradesBySeason, t.season).set(t.id, t);
    for (const a of snap.audit ?? []) this.bucket(this.auditBySeason, a.season).set(a.id, a);
    for (const d of snap.dockets ?? []) this.docketBySeason.set(d.season, d);
    for (const p of snap.teamPairings ?? []) {
      this.pairings.set(`${p.season}|${p.team.trim().toUpperCase()}`, p);
    }
    for (const m of snap.managers ?? []) this.roster.set(m.id.toUpperCase(), m);

    for (const what of ALL_COLLECTIONS) this.emit(what);
  }

  /** Throw away everything kept in this browser. The bundled data is untouched. */
  clearSaved(): void {
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      // Nothing to remove if storage was never available.
    }
    this.persistence.savedAt = null;
    this.persistence.bytes = 0;
  }

  /** How much of each thing is being kept, for the screen that reports it. */
  counts(): Array<{ what: string; n: number }> {
    const s = this.snapshot();
    return [
      { what: 'manager pairings', n: s.teamPairings.length },
      { what: 'managers added or amended', n: s.managers.length },
      { what: 'trades', n: s.trades.length },
      { what: 'keeper declarations', n: s.declarations.length },
      { what: 'draft-slot preferences', n: s.preferences.length },
      { what: 'FAAB entries', n: s.faab.length },
      { what: 'picks made', n: s.picks.length },
      { what: 'farm nominations', n: s.farmNominations.length },
      { what: 'rule proposals', n: s.proposals.length },
      { what: 'commissioner notes', n: s.notes.length },
      { what: 'interpretations', n: s.interpretations?.length ?? 0 },
      { what: 'commissioner overrides', n: s.overrides?.length ?? 0 },
      { what: 'mock keeper sets', n: s.mockKeepers?.length ?? 0 },
    ].filter((x) => x.n > 0);
  }

  private bucketBy<T>(m: Map<number, Map<GmId, T>>, season: number): Map<GmId, T> {
    let b = m.get(season);
    if (!b) {
      b = new Map();
      m.set(season, b);
    }
    return b;
  }

  private bucket<T>(m: Map<number, Map<string, T>>, season: number): Map<string, T> {
    let b = m.get(season);
    if (!b) {
      b = new Map();
      m.set(season, b);
    }
    return b;
  }

  private emit(what: StoreCollection) {
    for (const l of this.listeners) l(what);
    this.schedule();
  }

  subscribe(_season: number, onChange: Listener) {
    this.listeners.add(onChange);
    return () => {
      this.listeners.delete(onChange);
    };
  }

  /* -------------------------------------------------------------------------------------- */

  async settings(season: number) {
    return this.settingsBySeason.get(season) ?? null;
  }

  async saveSettings(s: LeagueSettings) {
    this.settingsBySeason.set(s.season, s);
    this.emit('settings');
  }

  /**
   * THE LEAGUE-VISIBLE SET — submitted slates only, the same as the Firestore store.
   *
   * Not a convenience filter. A slate a manager is still deciding on is his own business until he
   * sends it, and the rules enforce that on the real database. This store backs the offline build,
   * which is the draft-night fallback, so it has to answer the same question the same way — a
   * screen that shows drafts here and hides them there is a screen nobody can reason about.
   * `declarationFor` is how a manager reads his own.
   */
  async declarations(season: number) {
    return [...this.bucket(this.declarationsBySeason, season).values()]
      .filter((d) => d.status === 'submitted');
  }

  async declarationFor(season: number, gm: GmId) {
    return this.bucket(this.declarationsBySeason, season).get(gm) ?? null;
  }

  async saveDeclaration(d: KeeperDeclaration) {
    this.bucket(this.declarationsBySeason, d.season).set(d.gm, d);
    this.emit('declarations');
  }

  async preferences(season: number) {
    return [...this.bucket(this.prefsBySeason, season).values()];
  }

  /* -- proxy ballots (2015-I-1: a proxy counts toward the quorum of eight) ------------------ */

  async proxies(season: number) {
    return [...this.bucket(this.proxiesBySeason, season).values()];
  }

  async proxyFor(season: number, gm: GmId) {
    return this.bucket(this.proxiesBySeason, season).get(gm) ?? null;
  }

  async saveProxy(b: ProxyBallot) {
    this.bucket(this.proxiesBySeason, b.season).set(b.gm, b);
    this.emit('proxies');
  }

  /* -- a GM's own farm watch list, readable by nobody else ---------------------------------- */

  async farmTargetsFor(season: number, gm: GmId) {
    return this.bucket(this.farmTargetsBySeason, season).get(gm.toUpperCase() as GmId) ?? null;
  }

  async saveFarmTargets(t: FarmTargetList) {
    this.bucket(this.farmTargetsBySeason, t.season).set(t.gm.toUpperCase() as GmId, t);
    this.emit('farmTargets');
  }

  /* -- a GM's own picklist, readable by nobody else ------------------------------------------ */

  async pickListFor(season: number, gm: GmId) {
    return this.bucket(this.pickListsBySeason, season).get(gm.toUpperCase() as GmId) ?? null;
  }

  async savePickList(l: PickList) {
    this.bucket(this.pickListsBySeason, l.season).set(l.gm.toUpperCase() as GmId, l);
    this.emit('pickList');
  }

  async savePreference(p: DraftPreference) {
    this.bucket(this.prefsBySeason, p.season).set(p.gm, p);
    this.emit('preferences');
  }

  async trades(season: number) {
    return [...this.bucket(this.tradesBySeason, season).values()]
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  async tradesAll() {
    return [...this.tradesBySeason.values()]
      .flatMap((m) => [...m.values()])
      .sort((a, b) => b.date.localeCompare(a.date));
  }

  async saveTrade(t: Trade) {
    this.bucket(this.tradesBySeason, t.season).set(t.id, t);
    this.emit('trades');
  }

  async deleteTrade(season: number, id: string) {
    this.bucket(this.tradesBySeason, season).delete(id);
    this.emit('trades');
  }

  async faab(season: number) {
    return [...this.bucket(this.faabBySeason, season).values()]
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  async saveFaab(e: FaabEntry) {
    this.bucket(this.faabBySeason, e.season).set(e.id, e);
    this.emit('faab');
  }

  async deleteFaab(season: number, id: string) {
    this.bucket(this.faabBySeason, season).delete(id);
    this.emit('faab');
  }

  async farmNominations(season: number) {
    return [...this.bucket(this.farmBySeason, season).values()]
      .sort((a, b) => a.round - b.round || a.slot - b.slot);
  }

  async saveFarmNomination(n: FarmNomination) {
    this.bucket(this.farmBySeason, n.season).set(`${n.round}.${n.slot}`, n);
    this.emit('farmNominations');
  }

  async draftState(season: number) {
    return this.draftBySeason.get(season) ?? null;
  }

  async saveDraftState(s: DraftState) {
    this.draftBySeason.set(s.season, s);
    this.emit('draftState');
  }

  async picks(season: number) {
    return [...this.bucket(this.picksBySeason, season).values()]
      .sort((a, b) => a.overall - b.overall);
  }

  async savePick(p: DraftPick) {
    this.bucket(this.picksBySeason, p.season).set(String(p.overall), p);
    this.emit('picks');
  }

  async notes(season: number) {
    return [...this.bucket(this.notesBySeason, season).values()]
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async interpretations(season: number) {
    return [...this.bucket(this.interpBySeason, season).values()]
      .sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));
  }

  async saveInterpretation(i: InterpretationDoc) {
    this.bucket(this.interpBySeason, i.season).set(i.id, i);
    this.emit('interpretations');
  }

  async overrides(season: number) {
    return [...this.bucket(this.overrideBySeason, season).values()]
      .sort((a, b) => b.at.localeCompare(a.at));
  }

  async saveOverride(o: Override) {
    this.bucket(this.overrideBySeason, o.season).set(o.id, o);
    this.emit('overrides');
  }

  async mockKeepers(season: number, gm: GmId) {
    return this.bucketBy(this.mockKeepersBySeason, season).get(gm.toUpperCase()) ?? null;
  }

  async saveMockKeepers(m: MockKeeperSet) {
    this.bucketBy(this.mockKeepersBySeason, m.season).set(m.gm.toUpperCase(), m);
    this.emit('mockKeepers');
  }

  async saveNote(n: AdminNote) {
    this.bucket(this.notesBySeason, n.season).set(n.id, n);
    this.emit('notes');
  }

  async playerBank(season: number) {
    return this.bankBySeason.get(season) ?? null;
  }

  async savePlayerBank(b: PlayerBank) {
    this.bankBySeason.set(b.season, b);
    this.emit('playerBank');
  }

  async prospectBank(season: number) {
    return this.prospectsBySeason.get(season) ?? null;
  }

  async audit(season: number) {
    // Newest last here; the screen sorts. Insertion order is the honest one for an append-only log.
    return [...this.bucket(this.auditBySeason, season).values()]
      .sort((a, b) => a.at.localeCompare(b.at));
  }

  async recordAudit(e: AuditEntry) {
    /*
     * APPEND ONLY, and enforced here as well as in the rules — a memory store that let a row be
     * overwritten would make the offline build disagree with the deployed one about whether a log
     * can be edited, which is the one property a log has.
     */
    const bucket = this.bucket(this.auditBySeason, e.season);
    if (bucket.has(e.id)) return;
    bucket.set(e.id, e);
    this.emit('audit');
  }

  async liveTrades(season: number) {
    // Oldest first: a trade filed on Tuesday and one filed on Wednesday are read in the order
    // the league lived them, and the one closest to its deadline is at the top of the screen.
    return [...this.bucket(this.liveTradesBySeason, season).values()]
      .sort((a, b) => a.filedAt.localeCompare(b.filedAt));
  }

  async saveLiveTrade(t: LiveTradeDoc) {
    this.bucket(this.liveTradesBySeason, t.season).set(t.id, t);
    this.emit('liveTrades');
  }

  async deleteLiveTrade(season: number, id: string) {
    this.bucket(this.liveTradesBySeason, season).delete(id);
    this.emit('liveTrades');
  }

  async proposals(season: number) {
    return [...this.bucket(this.proposalsBySeason, season).values()]
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async saveProposal(p: RuleProposal) {
    this.bucket(this.proposalsBySeason, p.season).set(p.id, p);
    this.emit('proposals');
  }

  async deleteProposal(season: number, id: string) {
    this.bucket(this.proposalsBySeason, season).delete(id);
    this.emit('proposals');
  }

  async docket(season: number) {
    return this.docketBySeason.get(season) ?? null;
  }

  async saveDocket(d: Docket) {
    this.docketBySeason.set(d.season, d);
    this.emit('docket');
  }

  async teamPairings() {
    return [...this.pairings.values()].sort((a, b) => b.season - a.season || a.team.localeCompare(b.team));
  }

  async saveTeamPairing(p: TeamPairing) {
    this.pairings.set(`${p.season}|${p.team.trim().toUpperCase()}`, p);
    this.emit('teamPairings');
  }

  async deleteTeamPairing(season: number, team: string) {
    this.pairings.delete(`${season}|${team.trim().toUpperCase()}`);
    this.emit('teamPairings');
  }

  async avatars() {
    return [...this.faces.values()].sort((a, b) => a.gm.localeCompare(b.gm));
  }

  async saveAvatar(a: Avatar) {
    this.faces.set(a.gm.toUpperCase(), { ...a, gm: a.gm.toUpperCase() });
    this.emit('avatars');
  }

  async clearAvatar(gm: GmId) {
    this.faces.delete(gm.toUpperCase());
    this.emit('avatars');
  }

  async managers() {
    return [...this.roster.values()].sort((a, b) => a.id.localeCompare(b.id));
  }

  /** Offline there is no server to gate, but the toggle still has to remember its position. */
  private config: LeagueRoles | null = null;
  async leagueConfig(): Promise<LeagueRoles | null> { return this.config; }
  async saveLeagueConfig(c: LeagueRoles) { this.config = { ...c }; this.emit('managers'); }
  async hasSettings(season: number) { return (await this.settings(season)) !== null; }

  async saveManager(m: LeagueManager) {
    this.roster.set(m.id.toUpperCase(), { ...m, id: m.id.toUpperCase() });
    this.emit('managers');
  }

  async deleteManager(id: GmId) {
    this.roster.delete(id.toUpperCase());
    this.emit('managers');
  }

  /**
   * Everything in the store that names this manager.
   *
   * The question behind the delete button: is this a person with history, or a typo? Anything
   * returned here means the former, and the answer is retire rather than delete — deleting a
   * manager who is on a pairing would leave that pairing pointing at nobody.
   */
  usage(id: GmId): Array<{ what: string; n: number }> {
    const gm = id.toUpperCase();
    const s = this.snapshot();
    const flat = <T>(rows: T[], hit: (r: T) => boolean) => rows.filter(hit).length;
    return [
      { what: 'manager pairing', n: flat(s.teamPairings, (p) => p.gm.toUpperCase() === gm) },
      { what: 'trade', n: flat(s.trades, (t) => t.sides.some((x) => x.gm.toUpperCase() === gm)) },
      { what: 'keeper declaration', n: flat(s.declarations, (d) => d.gm.toUpperCase() === gm) },
      { what: 'draft-slot preference', n: flat(s.preferences, (p) => p.gm.toUpperCase() === gm) },
      { what: 'FAAB entry', n: flat(s.faab, (e) => e.gm.toUpperCase() === gm) },
      { what: 'pick', n: flat(s.picks, (p) => p.gm.toUpperCase() === gm) },
      { what: 'farm nomination', n: flat(s.farmNominations, (n) => n.nominator.toUpperCase() === gm
        || n.winner?.toUpperCase() === gm) },
      { what: 'rule proposal', n: flat(s.proposals, (p) => p.gm.toUpperCase() === gm) },
    ].filter((x) => x.n > 0);
  }

  async saveProspectBank(b: ProspectBank) {
    this.prospectsBySeason.set(2026, b);
    this.emit('prospectBank');
  }
}
